import { type ComponentPropsWithoutRef, useId } from "react";
import styles from "./Toggle.module.css";

/**
 * Toggle — switch / toggle primitive.
 *
 * Matches the Figma Toggle / Switch component set (fileKey
 * QkVMIm6TUdblppfqYlUtai, node 1107:946). Two sizes (sm, lg) × two
 * themes (light, dark via data-theme) × the usual interaction states
 * (default / hovered / focused / selected / disabled).
 *
 * Sizes:
 *   - `sm` — 28×16 track, 12×12 thumb, 2px inset when unselected
 *   - `lg` — 34×20 track, 16×16 thumb, 2px inset when unselected
 *
 * Uses a real `<input type="checkbox" role="switch">` so native
 * keyboard (Space toggles), click, form submission, and screen-reader
 * semantics all work. role="switch" is the ARIA-correct role for a
 * two-state on/off control (distinct from a plain checkbox).
 *
 * Sibling CSS selectors drive the visual state:
 *   - `.input:checked + .track` → violet accent fill
 *   - `.input:focus-visible + .track .thumb` → 4px focus ring
 *   - `.input:disabled + .track` → --icon-muted fill
 *
 * Labelling:
 *   - If `label` is provided, the primitive renders a wrapping
 *     `<label>` around the input + track + visible text. Clicking
 *     the text toggles the switch via native <label>/<input>.
 *   - If omitted, consumer supplies `aria-label` via rest spread.
 *
 * Colors (shared with Checkbox):
 *   - Accent: teal-600 (#0d9488) light / teal-300 (#5eead4) dark
 *     — matches --stroke-input-active; still hardcoded here pending
 *     a shared `--accent-brand` / `--checkbox-accent` token,
 *     see design-asks.md
 *   - Unselected hover: #d4d4d4 light / #737373 dark (neutral-300 /
 *     neutral-500) — no Figma alias
 *   - Disabled: --icon-muted (#a3a3a3) light / --bg-tertiary (#404040)
 *     dark — these map cleanly
 *   - Focus ring stops are 4px halo (distinct from Checkbox's 2px) —
 *     flagged in design-asks alongside the Checkbox ring gap
 */

type Size = "sm" | "lg";

export type ToggleProps = {
  /** Whether the switch is on. */
  checked: boolean;
  /** Fires with the new checked value when the user toggles the switch. */
  onChange: (checked: boolean) => void;
  /** Visual size. Defaults to `sm`. */
  size?: Size;
  /** Visible text label. When omitted, consumer supplies aria-label via rest. */
  label?: string;
  className?: string;
} & Omit<
  ComponentPropsWithoutRef<"input">,
  "type" | "role" | "checked" | "onChange" | "className" | "size" | "aria-checked"
>;

export function Toggle({
  checked,
  onChange,
  size = "sm",
  label,
  disabled,
  id,
  className,
  ...rest
}: ToggleProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  const wrapperClasses = [styles.wrapper, styles[`size-${size}`], className]
    .filter(Boolean)
    .join(" ");

  return (
    <label className={wrapperClasses} htmlFor={inputId}>
      <input
        id={inputId}
        type="checkbox"
        role="switch"
        aria-checked={checked}
        className={styles.input}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        {...rest}
      />
      <span className={styles.track} aria-hidden="true">
        <span className={styles.thumb} />
      </span>
      {label && <span className={styles.label}>{label}</span>}
    </label>
  );
}
